import { useEffect, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildSubmissionSchema,
  type FormDefinition,
} from "@colab/shared";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { useFormDetail } from "./use-form-detail";
import { useSaveDraft } from "./use-save-draft";
import { useSubmitForm } from "./use-submit-form";
import { useDeleteDraft } from "./use-delete-draft";
import { useUploadFormAttachment } from "./use-upload-form-attachment";
import { useRemoveFormAttachment } from "./use-remove-form-attachment";
import type {
  FormAttachment,
  PortalFormDraftDetail,
  PortalFormDetailResponse,
} from "./types";

// Orchestrator hook — the marquee piece of Phase 7 sub-bucket 2. Wires:
//   • useFormDetail(slug) for the form + current draft hydration
//   • react-hook-form with zodResolver(buildSubmissionSchema(fields)) so the
//     SAME validator the worker runs at submit time also gates client-side
//     field-level errors. Divergence here means the client says "valid" but
//     the server says "invalid".
//   • useSaveDraft + a 500ms debounced callback (mirrors MDF's bucket 9
//     local-state-only auto-save) — RHF owns the field values during edit;
//     the save mutation is fire-and-track-status, no cache invalidation.
//   • useSubmitForm for the final promote (the page invokes this on the
//     submit button click against react-hook-form's handleSubmit).
//   • useUploadFormAttachment / useRemoveFormAttachment / useDeleteDraft
//     for the surrounding lifecycle.
//
// The "Saving / Saved / Error" indicator comes from `save.isPending /
// .isSuccess / .isError`. Saved state is sticky until the next save fires.
//
// Phase 7 sub-bucket 2b's form-detail-page consumes this directly; the page
// itself stays under ~200 lines.

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export type FormRuntime = {
  form: UseFormReturn<Record<string, unknown>>;
  detail: ReturnType<typeof useFormDetail>;
  draft: PortalFormDraftDetail | undefined;
  attachments: FormAttachment[];
  saveStatus: SaveStatus;
  save: ReturnType<typeof useSaveDraft>;
  submit: ReturnType<typeof useSubmitForm>;
  discard: ReturnType<typeof useDeleteDraft>;
  upload: ReturnType<typeof useUploadFormAttachment>;
  remove: ReturnType<typeof useRemoveFormAttachment>;
};

export function useFormRuntime(slug: string): FormRuntime {
  const detail = useFormDetail(slug);
  const draft = detail.data?.draft;
  const submissionId = draft?.submissionId ?? null;

  const save = useSaveDraft(submissionId);
  const submit = useSubmitForm(slug, submissionId);
  const discard = useDeleteDraft(slug);
  const upload = useUploadFormAttachment(slug, submissionId);
  const remove = useRemoveFormAttachment(slug);

  // Build the form-wide Zod schema from the live definition. Re-derives
  // when the form definition reference changes (e.g. admin republishes).
  const schema = useMemo(() => {
    if (!detail.data?.form.definition) return null;
    return buildSubmissionSchema(
      detail.data.form.definition as FormDefinition,
    );
  }, [detail.data?.form.definition]);

  // Default values: hydrate from draft.payload + per-field empty defaults
  // from the field definition. The buildSubmissionSchema validator already
  // tolerates `null`/empty for non-required fields via its preprocess wrap.
  const defaults = useMemo<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {};
    if (!detail.data) return out;
    for (const section of detail.data.form.definition.sections) {
      for (const field of section.fields) {
        if (field.kind === "section_heading") continue;
        // Match the legacy FIELD_REGISTRY default-value semantics: empty
        // string for text-likes, empty array for multi-select, false for
        // checkbox, null otherwise. Draft payload takes precedence.
        const fromDraft = draft?.payload?.[field.name];
        if (fromDraft !== undefined) {
          out[field.name] = fromDraft;
          continue;
        }
        switch (field.kind) {
          case "text":
          case "long_text":
          case "email":
          case "date":
          case "partner_lookup":
            out[field.name] = "";
            break;
          case "number":
            out[field.name] = null;
            break;
          case "select":
            out[field.name] = field.multiple ? [] : "";
            break;
          case "checkbox":
            out[field.name] = false;
            break;
          case "file":
            out[field.name] = null;
            break;
        }
      }
    }
    return out;
  }, [detail.data, draft?.payload]);

  const form = useForm<Record<string, unknown>>({
    // schema may be null on first render before detail loads; the resolver
    // call is skipped if schema is null (zodResolver tolerates undefined
    // but we pass a permissive fallback to keep types tight).
    resolver: schema ? zodResolver(schema as never) : undefined,
    defaultValues: defaults,
    mode: "onBlur",
  });

  // Reset the form when the underlying draft loads / changes (navigating
  // between forms or after submit). Without this the defaults from the
  // first render stick even when the draft hydrates later.
  useEffect(() => {
    if (detail.data?.draft) {
      form.reset(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data?.draft?.submissionId]);

  // Debounced auto-save. RHF's watch fires per field edit; the latest values
  // win and are POSTed after 500ms of inactivity.
  const debouncedSave = useDebouncedCallback(
    (values: Record<string, unknown>) => {
      if (!submissionId) return;
      save.mutate({ payload: values });
    },
    500,
  );

  useEffect(() => {
    const sub = form.watch((values) => {
      // RHF returns FieldValues, which is a Partial. Cast to plain object;
      // any null/undefined fields are handled by the auto-save body schema's
      // preprocess.
      debouncedSave(values as Record<string, unknown>);
    });
    return () => sub.unsubscribe();
  }, [form, debouncedSave]);

  // Derive a stable saveStatus. Mutation state is the source of truth.
  let saveStatus: SaveStatus = "idle";
  if (save.isPending) saveStatus = "saving";
  else if (save.isError) saveStatus = "error";
  else if (save.isSuccess) saveStatus = "saved";

  return {
    form,
    detail,
    draft,
    attachments: draft?.attachments ?? [],
    saveStatus,
    save,
    submit,
    discard,
    upload,
    remove,
  };
}

// Re-export the response type so callers don't need to import from types.ts
// to satisfy TS when consuming detail.data.
export type { PortalFormDetailResponse };
